import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import PermissionForm from "../../components/forms/PermissionForm";
import {
  Permission,
  PermissionCreateFormData,
  PermissionUpdateFormData,
} from "../../schemas/permissionSchema";
import { usePermissionsInfinite } from "../../hooks/usePermissionsInfinite";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";

const PermissionsList: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] =
    useState<Permission | null>(null);
  const [search, setSearch] = useState("");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const {
    permissions,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createPermission,
    deletePermissions,
  } = usePermissionsInfinite({ search });

  const { canDelete, canCreate, canView } = useAuthPermissions("permissions");

  const handleDelete = (permission: Permission) => {
    if (
      window.confirm(
        `Are you sure you want to delete permission "${permission.action}"?`,
      )
    ) {
      deletePermissions([permission.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${ids.length} permission(s)?`,
      )
    ) {
      deletePermissions(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = (
    data: PermissionCreateFormData | PermissionUpdateFormData,
  ) => {
    createPermission(data as PermissionCreateFormData);
    setIsModalOpen(false);
    setSelectedPermission(null);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedPermission(null);
  };

  const columns: ColumnDef<Permission>[] = [
    {
      accessorKey: "action",
      header: "Action",
      cell: ({ getValue }) => {
        const action = getValue() as string;
        const [module, operation] = action.split(".");
        return (
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">{module}</span>
            <span className="text-sm text-gray-500 capitalize">
              {operation}
            </span>
          </div>
        );
      },
    },
  ];

  const actions = [
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
      visible: canDelete,
    },
  ];

  return (
    <div className="flex flex-col px-6 py-2">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-800">Permissions</h1>
        {canCreate && (
          <button
            onClick={() => {
              setSelectedPermission(null);
              setIsModalOpen(true);
            }}
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
          >
            <PlusIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {canView && (
        <Table
          data={permissions as Permission[]}
          columns={columns}
          actions={actions}
          searchable={true}
          pagination={true}
          isLoading={isLoading}
          maxHeight="66vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          onSearch={setSearch}
          selectable={canDelete}
          getRowId={(permission) => permission.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={
            canDelete
              ? [
                  {
                    label: "Delete Selected",
                    icon: <TrashIcon className="h-4 w-4" />,
                    onClick: handleBulkDelete,
                    variant: "danger" as const,
                  },
                ]
              : undefined
          }
        />
      )}

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title={selectedPermission ? "Edit Permission" : "Create Permission"}
          size="md"
        >
          <PermissionForm
            initialData={selectedPermission || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={false}
            isUpdate={selectedPermission !== null}
          />
        </Modal>
      )}
    </div>
  );
};

export default PermissionsList;
